import { ApiRequest, ShowLoader, getComponentAsSvg } from "GlobalFunctions";
import { IoIosAttach } from "react-icons/io";
import {getProductQuantity} from "components/Bag/Bag";
import { checkIfCouponExists } from "../../utils/couponValidator";
import { calculateTaxes } from "components/Orders/CalculateTaxes";

/*=============================
CALCULATE SUBTOTAL
=============================*/
function calculateSubtotal(products: any[]): number {
    if (!products || products.length === 0) return 0;
    
    const total = products.reduce((total: number, product: any) => {
        let price = parseFloat(product.product_price) || 0;
        const quantity = parseInt(product.product_quantity) || 0;
        const taxAmount = parseFloat(product.tax_amount) || 0;
        const taxIncluded = product.tax_included || false;
        

        
        let productTotal = Math.round((price * quantity) * 100) / 100; // Round to 2 decimals
        
        // Add bundle products prices to the total
        if (product.bundle_products && product.bundle_products.length > 0) {
            const bundleTotal = product.bundle_products.reduce((bundleSum: number, bundle: any) => {
                let bundlePrice = parseFloat(bundle.price) || 0;
                let quantity = parseInt(bundle.quantity) || 1; 
                
                return Math.round((bundleSum + bundlePrice * quantity) * 100) / 100; // Round to 2 decimals
            }, 0);
            productTotal = Math.round((productTotal + (bundleTotal * quantity)) * 100) / 100; // Round to 2 decimals
        }
        
        return Math.round((total + productTotal) * 100) / 100; // Round to 2 decimals
    }, 0);
    
    return Math.round(total * 100) / 100; // Final rounding to ensure 2 decimals
}

/*=============================
CALCULATE TOTAL PRICE
=============================*/
const calculateTotalPrice = async (cart: any) => {
    let totalPrice = cart.subtotal_price || 0;
    totalPrice += cart.shipping_price || 0;
    totalPrice += cart.total_tax || 0;
    totalPrice -= cart.total_discount || 0;

    cart.total_price = totalPrice;
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Dispatch a custom event to notify that the cart has been updated
    // This will be caught by the Checkout component to trigger payment form loading
    
    // const event = new CustomEvent('cart:updated');
    // document.dispatchEvent(event);
    

    return totalPrice;
}

/*=============================
GET ORDER DETAILS
=============================*/
/**
 * @param {"cart"|"bag"|"checkout"|"invoice"} context - Quién llama la función para filtrar variables (opcional, default: "cart")
 */
export default async function getOrderDetails(context = "cart") {
    let cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
    const cartTotalElement = document.querySelector('[pc="summary-subtotal"]');
    
    if(cart.coupon_id && Number(cart.coupon_id) != 0 && cart.coupon) {
        await checkIfCouponExists(cart.coupon_code)
        cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
    }

    /*=============================
    CHECK IF ORDER ID EXISTS
    ==============================*/
    const is_cart = cart.products.length > 0;
    if (!is_cart) {
        
        //if context = cart redirect to home
        if (context === "cart") {
            localStorage.removeItem('cart');
            localStorage.setItem('productsQuantity', '0');
            window.location.href = '/';
            return;
        }

        if (cartTotalElement) {
            cartTotalElement.innerHTML = '$0';
        }
        return;
    }

    // Calculate the subtotal based on products
    cart.subtotal_price = calculateSubtotal(cart.products);
    localStorage.setItem('cart', JSON.stringify(cart));

    await renderCartProducts(cart.products, context);
    
    if (cartTotalElement) {
        cartTotalElement.innerHTML = '$' + Number(cart.subtotal_price ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
    }

    setSummary();
    
    // Dispatch cart:updated event after all calculations are complete
    const event = new CustomEvent('cart:updated');
    document.dispatchEvent(event);
    
    return cart;
}





const removeCoupon = async (coupon_code: string) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '{}');
    cart.coupon_id = 0;
    cart.coupon_code = '';
    cart.total_discount = 0;
    cart.total_price = cart.subtotal_price + cart.shipping_price + cart.total_tax;
    //remove product_discount from each product
    cart.products.forEach((product: any) => {
        product.product_discount = 0;
    });
    
    delete cart.coupon;

    localStorage.setItem('cart', JSON.stringify(cart));
    calculateTaxes(cart);
} 





/*=============================
RENDER CART PRODUCTS
==============================*/

export const renderCartProducts = async (products: any, context = "cart") => {
    const cartProductsContainer = document.querySelector('[pc="product-list"]');
    if (!cartProductsContainer) return;

    const productModel = document.querySelector('[pc="product-item"]');
    if (!productModel) return;
    const productModelCloneForRemoval = productModel.cloneNode(true) as HTMLElement;

    // Get bundle products template from the product model for cloning
    let bundleModelCloneForRemoval: HTMLElement | null = null;
    const bundleContainer = productModelCloneForRemoval.querySelector('[pc-bundle="list"]');
    if (bundleContainer) {
        const bundleModel = bundleContainer.querySelector('[pc-bundle="item"]');
        if (bundleModel) {
            bundleModelCloneForRemoval = bundleModel.cloneNode(true) as HTMLElement;
        }
    }

    cartProductsContainer.innerHTML = '';

    // Obtener configuración de variables a ocultar
    let hideVariables: Record<string, string> = {};
    try {
        const storeConfigRaw = localStorage.getItem('storeConfiguration');
        if (storeConfigRaw) {
            const storeConfig = JSON.parse(storeConfigRaw);
            const variablesArr = storeConfig?.data?.logic?.hide_variables?.variables;
            if (Array.isArray(variablesArr)) {
                // Construir un mapa: variable -> regla para el contexto
                variablesArr.forEach((v: any) => {
                    if (v.variable && v[context]) {
                        hideVariables[v.variable] = v[context];
                    }
                });
            }
        }
    } catch (e) {
        // Si hay error, no filtrar nada
        hideVariables = {};
    }

    /* MAP PRODUCTS
    ==============================*/
    products.forEach((product: any) => {
        const productClone = productModelCloneForRemoval.cloneNode(true) as HTMLElement;
        const bundleClone = bundleModelCloneForRemoval ? bundleModelCloneForRemoval.cloneNode(true) as HTMLElement : null;

        // Filtrar custom_inputs según hideVariables/context
        let filteredCustomInputs = product.custom_inputs;
        if (Array.isArray(filteredCustomInputs) && Object.keys(hideVariables).length > 0) {
            filteredCustomInputs = filteredCustomInputs.filter((attr: any) => {
                // Si la variable está en hideVariables y es "show" para el contexto, mostrarla
                if (hideVariables.hasOwnProperty(attr.name)) {
                    return hideVariables[attr.name] === "show";
                }
                // Si no está en la config, mostrarla por defecto
                return true;
            });
        }

        productClone.querySelector('[pc="product-name"]')!.innerHTML = product.product_name;
        productClone.querySelector('[pc="product-price"]')!.innerHTML = '$' + Number(product.product_price).toLocaleString('en-US', { minimumFractionDigits: 2 });
        
        
        /* QUANTITY SELECT
        ==============================*/
        const quantityOptions = Array.from({ length: 10 }, (_, i) => i + 1).map(number => (
            `<option value="${number}">${number}</option>`
        )).join('');

        const quantitySelectElement = productClone.querySelector('[pc="product-quantity"]');

        if (quantitySelectElement) {

            const classes = quantitySelectElement?.getAttribute('class');

            if (quantitySelectElement.tagName.toLowerCase() !== 'select') {
                const quantitySelect = `<select pc="product-quantity" class="${classes}" name="product-quantity">
                    ${quantityOptions}
                </select>`;
                quantitySelectElement.outerHTML = quantitySelect;
            }

            const updatedQuantitySelectElement = productClone.querySelector('[pc="product-quantity"]') as HTMLSelectElement;
            updatedQuantitySelectElement.innerHTML = quantityOptions;
            updatedQuantitySelectElement.value = String(product.product_quantity);
            updatedQuantitySelectElement.addEventListener('change', async () => {
                await updateProductQuantity(product.order_product_id, updatedQuantitySelectElement.value);
            });
        } 


        /* IMAGE 
        ============================== */
        const productImageElement = productClone.querySelector('[pc="product-image"]') as HTMLImageElement;

        if (productImageElement) {
            productClone.querySelector('[pc="product-image"]')!.setAttribute('src', product.   product_image);
            productClone.querySelector('[pc="product-image"]')!.removeAttribute('srcset');
        }

        /* CUSTOM INPUTS
        ============================== */
        const custom_inputs = filteredCustomInputs
            .filter((attr: any) => attr.value !== "" && attr.value !== undefined && attr.value !== null)
            .map((attr: any) => {
                if (attr.name === 'files') {
                    try {
                        const files = attr.value
                        return `Files: ` + files.map((file: any) => `
                            <a href="${file.url}" target="_blank" style="color: inherit; text-decoration: underline;">${file.name}</a>`
                        ).join(',');
                    } catch (error) {
                        console.error('Error parsing files:', error);
                        return ''; // Return empty string if parsing fails
                    }
                }
                return `${attr.name}: ${attr.value}`;
            })
            .join('<br>');

        const customInputsElement = productClone.querySelector('[pc="product-custom"]')

        if (customInputsElement) {
            customInputsElement.innerHTML = custom_inputs;
        }

        

        /* REMOVE BUTTON
        ============================== */
        const removeButtonElement = productClone.querySelector('[pc="remove-product"]') as HTMLElement;

        if (removeButtonElement) {
            removeButtonElement.setAttribute('data-type', 'main');
            removeButtonElement.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await removeProduct(product.order_product_id, context);
            });
        } 
        



        /* CHILD PRODUCT
        ============================== */
        if (product.parent_product_id > 0) {
            productClone.classList.add('product-child');
        }

        /* BUNDLE PRODUCTS CONTAINER CLEANUP
        ============================== */
        // Always clear the bundle container for this specific product, regardless of whether it has bundle products
        const productBundleContainer = productClone.querySelector('[pc-bundle="list"]');
        if (productBundleContainer) {
            productBundleContainer.innerHTML = '';
        }

        /* BUNDLE SHOW/HIDE BUTTON SETUP
        ============================== */
        const bundleShowHideButton = productClone.querySelector('[pc-bundle="show-hide"]') as HTMLElement;
        if (bundleShowHideButton && product.bundle_products && product.bundle_products.length > 0) {
            const bundleCount = product.bundle_products.length;
            const productId = product.order_product_id;
            
            // Get bundle visibility state from localStorage
            const bundleVisibilityKey = 'bundleVisibility';
            let bundleVisibilityState: Record<string, boolean> = {};
            try {
                const stored = localStorage.getItem(bundleVisibilityKey);
                if (stored) {
                    bundleVisibilityState = JSON.parse(stored);
                }
            } catch (e) {
                console.error('Error parsing bundle visibility state:', e);
                bundleVisibilityState = {};
            }
            
            // Get current state for this product (default to visible if not found)
            let isVisible = bundleVisibilityState[productId] !== undefined ? bundleVisibilityState[productId] : true;
            
            // Set initial button text and container visibility based on stored state
            bundleShowHideButton.innerHTML = isVisible ? `Hide ${bundleCount} products` : `Show ${bundleCount} products`;
            if (productBundleContainer) {
                (productBundleContainer as HTMLElement).style.display = isVisible ? '' : 'none';
            }
            
            // Add click event listener
            bundleShowHideButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                if (productBundleContainer) {
                    isVisible = !isVisible;
                    
                    // Update UI
                    if (isVisible) {
                        (productBundleContainer as HTMLElement).style.display = '';
                        bundleShowHideButton.innerHTML = `Hide ${bundleCount} products`;
                    } else {
                        (productBundleContainer as HTMLElement).style.display = 'none';
                        bundleShowHideButton.innerHTML = `Show ${bundleCount} products`;
                    }
                    
                    // Save state to localStorage
                    bundleVisibilityState[productId] = isVisible;
                    localStorage.setItem(bundleVisibilityKey, JSON.stringify(bundleVisibilityState));
                }
            });
        } else if (bundleShowHideButton) {
            // Hide the button if there are no bundle products
            bundleShowHideButton.style.display = 'none';
        }

        // First append the main product
        cartProductsContainer.appendChild(productClone);

        /* BUNDLE PRODUCTS
        ============================== */
        if (product.bundle_products && product.bundle_products.length > 0 && bundleModelCloneForRemoval && productBundleContainer) {
            console.log('Rendering bundle products for parent:', product.order_product_id, product.bundle_products);
            
            product.bundle_products.forEach((bundle: any) => {
                console.log('Rendering bundle:', bundle);
                
                if (!bundleModelCloneForRemoval) return; // Additional null check
                const bundleClone = bundleModelCloneForRemoval.cloneNode(true) as HTMLElement;
                bundleClone.classList.add('bundle-product');
                bundleClone.setAttribute('data-bundle-id', bundle.id);
                bundleClone.setAttribute('data-parent-id', product.order_product_id);
                
                bundleClone.querySelector('[pc="product-name"]')!.innerHTML = bundle.name;
                
                const skuElement = bundleClone.querySelector('[pc="product-sku"]');
                if (skuElement) {
                    skuElement.innerHTML = bundle.sku || '';
                }
                
                // Render SKU in product-custom element for bundle products
                const customElement = bundleClone.querySelector('[pc="product-custom"]');
                if (customElement) {
                    customElement.innerHTML = bundle.sku || '';
                }
                
                bundleClone.querySelector('[pc="product-price"]')!.innerHTML = '$' + Number(bundle.price).toLocaleString('en-US', { minimumFractionDigits: 2 });
                bundleClone.querySelector('[pc="product-image"]')!.setAttribute('src', bundle.image);
                bundleClone.querySelector('[pc="product-image"]')!.removeAttribute('srcset');
                
                // Remove quantity selector for bundle products as they inherit parent quantity
                const bundleQuantityElement = bundleClone.querySelector('[pc="product-quantity"]');
                if (bundleQuantityElement) {
                    bundleQuantityElement.remove();
                }
                
                // Add remove button functionality for bundle products
                const bundleRemoveButtonElement = bundleClone.querySelector('[pc="remove-product"]') as HTMLElement;
                if (bundleRemoveButtonElement) {
                    bundleRemoveButtonElement.setAttribute('data-type', 'bundle');
                    bundleRemoveButtonElement.addEventListener('click', async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Bundle remove button clicked for:', { parentId: product.order_product_id, bundleId: bundle.id });
                        await removeBundleProduct(product.order_product_id, bundle.id, context);
                    });
                }
                
                // Append bundle product to this specific product's bundle container
                productBundleContainer.appendChild(bundleClone);
            });
        }
        
        
    });
};




/*=============================
UPDATE PRODUCT QUANTITY
=============================*/
export async function updateProductQuantity(order_product_id: string, quantity: string) {
    console.log('order_product_id', order_product_id);
    console.log('quantity', quantity);
    const cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}'); 

    let is_bundle_products_active = false;
    const storeConfigRaw = localStorage.getItem('storeConfiguration');
    if (storeConfigRaw) {
        const storeConfig = JSON.parse(storeConfigRaw);
        const is_bundle_products_active = storeConfig?.data?.logic?.bulk_tiered_discounts?.is_active;
    }
    
    // Find the product in the cart
    const productIndex = cart.products.findIndex((product: any) => product.order_product_id === order_product_id);
    
    if (productIndex !== -1) {
        const product = cart.products[productIndex];
        
        // Update quantity
        cart.products[productIndex].product_quantity = quantity;
        
        // Check for bulk tiered discounts
        if (product.bulk_tiered_discounts && is_bundle_products_active) {
            const tiers = product.bulk_tiered_discounts.split(',').map((tier: string) => {
                const [price, qty] = tier.split(':');
                return { price, quantity: parseInt(qty) };
            }).sort((a: any, b: any) => a.quantity - b.quantity); // Sort by quantity ascending
            
            const qtyNumber = parseInt(quantity);
            let newPrice = product.product_price; // Default to current price
            
            // Find appropriate price tier
            if (tiers.length > 0) {
                // If quantity is less than smallest tier, use first tier price
                if (qtyNumber <= tiers[0].quantity) {
                    newPrice = tiers[0].price;
                } 
                // If quantity is larger than largest tier, use last tier price
                else if (qtyNumber >= tiers[tiers.length - 1].quantity) {
                    newPrice = tiers[tiers.length - 1].price;
                } 
                // Find the matching tier or closest lower tier
                else {
                    for (let i = 0; i < tiers.length - 1; i++) {
                        if (qtyNumber >= tiers[i].quantity && qtyNumber < tiers[i + 1].quantity) {
                            newPrice = tiers[i].price;
                            break;
                        }
                    }
                }
                
                // Update product price
                cart.products[productIndex].product_price = newPrice;
            }
        }
        
        
        // Update cart in localStorage
        localStorage.setItem('cart', JSON.stringify(cart));
        
        // Refresh order details
        await getOrderDetails();
    }
}


/*=============================
REMOVE PRODUCT
=============================*/
export async function removeProduct(order_product_id: string, context = "cart") {
    // Get cart from localStorage
    const cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
    
    // Find and remove the product from the cart
    const productIndex = cart.products.findIndex((product: any) => product.order_product_id === order_product_id);
    
    if (productIndex !== -1) {
        // Remove the product
        cart.products.splice(productIndex, 1);
        
        // Recalculate subtotal
        cart.subtotal_price = calculateSubtotal(cart.products);
        
        // Update cart in localStorage
        localStorage.setItem('cart', JSON.stringify(cart));
        
        getOrderDetails(context);
        getProductQuantity();
    }
    
}


/*=============================
REMOVE BUNDLE PRODUCT
=============================*/
export async function removeBundleProduct(parentProductId: string, bundleProductId: string, context = "cart") {
    console.log('removeBundleProduct called with:', { parentProductId, bundleProductId });
    
    // Get cart from localStorage
    const cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
    
    // Find the parent product
    const parentProductIndex = cart.products.findIndex((product: any) => product.order_product_id === parentProductId);
    
    console.log('Found parent product at index:', parentProductIndex);
    
    if (parentProductIndex !== -1 && cart.products[parentProductIndex].bundle_products) {
        console.log('Bundle products before removal:', cart.products[parentProductIndex].bundle_products);
        
        // Remove the specific bundle product
        cart.products[parentProductIndex].bundle_products = cart.products[parentProductIndex].bundle_products.filter(
            (bundle: any) => bundle.id !== bundleProductId
        );
        
        console.log('Bundle products after removal:', cart.products[parentProductIndex].bundle_products);
        
        // Check if all bundle products have been removed
        if (cart.products[parentProductIndex].bundle_products.length === 0) {
            console.log('All bundle products removed, removing parent product');
            // Remove the parent product entirely when no bundle products are left
            cart.products.splice(parentProductIndex, 1);
        }
        
        // Recalculate subtotal
        cart.subtotal_price = calculateSubtotal(cart.products);
        
        // Update cart in localStorage
        localStorage.setItem('cart', JSON.stringify(cart));
        
        // Refresh order details
        await getOrderDetails(context);
        getProductQuantity();
    } else {
        console.log('Parent product not found or no bundle products');
    }
}


export const setSummary = async () => {

    const cart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
    const taxIncluded = cart.tax_included || false;

    //SUBTOTAL
    const subtotalElement = document.querySelector('[pc="summary-subtotal"]');
    if (subtotalElement) {
        subtotalElement.innerHTML = '$' + Number(cart.subtotal_price).toLocaleString('en-US', { minimumFractionDigits: 2 });
    }

    //DELIVERY
    const deliveryElement = document.querySelector('[pc="summary-delivery"]');
    if (deliveryElement) {
        deliveryElement.innerHTML =  "$" + Number(cart.shipping_price || "0").toLocaleString('en-US', { minimumFractionDigits: 2 }) 
    }

    //TAX
    const taxElement = document.querySelector('[pc="summary-tax"]');
    if (taxElement) {
        taxElement.innerHTML = '$' + Number(cart.total_tax || "0").toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }




    if (cart.coupon_id && Number(cart.coupon_id) != 0) {
        const discountElement = document.querySelector('[pc="summary-discount-value"]');
        if (discountElement) {
            discountElement.innerHTML = '-$' + Number(cart.total_discount).toLocaleString('en-US', { minimumFractionDigits: 2 });
        }

        const coupon_code = cart.coupon_code;
        const summary_discount_label = document.querySelector('[pc="summary-discount-label"]');
        if (summary_discount_label) {
            // Eliminar el contenido previo si ya existía
            summary_discount_label.innerHTML = `
                Discount <span 
                class='coupon-chip'
                >${coupon_code} 
                <a
                class='remove-coupon'
                href="#" style="cursor: pointer; margin-left: 5px;">
                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 48 48" style="enable-background:new 0 0 48 48;" xml:space="preserve">
                <path d="M45.8,0c-0.6,0-1.1,0.2-1.5,0.6L24,21L3.6,0.7C3.2,0.2,2.7,0,2.1,0C1,0,0,1,0,2.1c0,0.6,0.2,1.1,0.6,1.5L21,24L0.7,44.4  c-0.8,0.8-0.9,2.1-0.1,3s2.1,0.9,3,0.1c0,0,0,0,0.1-0.1L24,27l20.4,20.4c0.8,0.8,2.1,0.9,3,0.1s0.9-2.1,0.1-3c0,0,0,0-0.1-0.1L27,24  L47.3,3.6c0.8-0.8,0.9-2.1,0.1-3C47,0.2,46.4,0,45.8,0z"/>
                </svg>
                </a></span>
            `;

            // Agregar el listener al botón "x"
            const removeLink = summary_discount_label.querySelector('a');
            if (removeLink) {
                removeLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    removeCoupon(coupon_code);
                });
            }
        }
    } 
    else {
        showHideCouponContainer()

    }
    
    const totalPrice = await calculateTotalPrice(cart);
    const totalElement = document.querySelector('[pc="summary-total"]');
    if (totalElement) {
        totalElement.innerHTML = '$' + totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2 });
    }
};





const showHideCouponContainer = () => {
    const discountElement = document.querySelector('[pc="summary-discount-value"]');
    if (discountElement) {
        discountElement.innerHTML = '$0.00';
    }
    //coupon-chip
    const summary_discount_label = document.querySelector('[pc="summary-discount-label"]');
    if (summary_discount_label) {
        summary_discount_label.innerHTML = `
            Discount
        `;
    }

    const coupon_form = document.querySelector('[pc="discount-form"]');
    if (coupon_form) {
        coupon_form.classList.remove('hidden');
    }

    const coupon_wrapper = document.querySelector('[pc="discount-wrapper"]');
    if (coupon_wrapper) {
        coupon_wrapper.classList.remove('hidden');
    }

    const toast_discount_ok = document.querySelector('[pc="toast-discount-ok"]');
    const toast_discount_error = document.querySelector('[pc="toast-discount-error"]');
    if (toast_discount_ok) {
        toast_discount_ok.classList.add('hidden');
    }
    if (toast_discount_error) {
        toast_discount_error.classList.add('hidden');
    }
}